/**
 * The parts the sales ledger's document pages share (docs/features/
 * order-to-invoice/80_finance_sales_ledger_sl4_update_plan.md commit 7): an
 * invoice, a pro-forma and a credit note as a page, each with its header, the
 * money against it and the payments and credit notes that touch it.
 *
 * No mock-up was drawn for these pages; they are the smallest pages
 * consistent with the Invoice step's rows and the Awaiting payment page. Every
 * word of a state is the backend's (views' `sales_documents.py`); this file
 * only chooses a badge colour for it and lays the facts out.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  OrderCreditNoteView,
  OrderPaymentReceivedView,
} from "@shared-core/data/order-to-invoice-types";
import type { DocumentHeader } from "@shared-core/data/sales-ledger-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { orderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { paths } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Card } from "../../primitives/card";
import { Skeleton } from "../../primitives/skeleton";

type BadgeVariant = "success" | "warning" | "error" | "neutral";

/** A document's state, as a badge colour: done is green, waiting on finance amber, trouble red, the rest grey. */
const STATE_VARIANTS: Record<string, BadgeVariant> = {
  paid: "success",
  applied: "success",
  refunded: "success",
  awaiting_payment: "neutral",
  approved_in_xero: "neutral",
  issued: "neutral",
  part_paid: "warning",
  held: "warning",
  draft_in_xero: "warning",
  refund_pending: "warning",
  refund_failed: "error",
  voided_in_xero: "error",
  voided: "error",
  expired: "error",
  superseded: "neutral",
};

export function StateBadge({ state, label }: { state: string; label: string }) {
  return (
    <Badge variant={STATE_VARIANTS[state] ?? "neutral"} className="font-normal">
      {label}
    </Badge>
  );
}

/** The document's header facts: whose it is, which order, who pays. */
export function headerFacts(
  header: DocumentHeader,
): { label: string; value: ReactNode }[] {
  const orderNo = header.order_no;
  return [
    // A document of no order (a period charge's invoice) has no Order fact.
    ...(orderNo
      ? [
          {
            label: "Order",
            value: (
              <Link
                to={paths.orderDetail({ orderNo })}
                className="hover:underline"
              >
                {orderNo}
              </Link>
            ),
          },
        ]
      : []),
    {
      label: "Customer",
      value: (
        <Link
          to={paths.customerInvoices({ customerId: header.customer_code })}
          className="hover:underline"
        >
          {header.customer_name ?? header.customer_code}
        </Link>
      ),
    },
    ...(header.payer_name && header.payer_name !== header.customer_name
      ? [{ label: "Paid by", value: header.payer_name }]
      : []),
    ...(header.customer_order_number
      ? [{ label: "Your PO", value: header.customer_order_number }]
      : []),
  ];
}

/** Rows of label and money, the last one set apart as the total. */
export function MoneySummary({
  rows,
  ariaLabel,
}: {
  rows: { label: string; pence: number; emphasis?: boolean; muted?: boolean }[];
  ariaLabel: string;
}) {
  return (
    <dl
      aria-label={ariaLabel}
      className="grid max-w-sm grid-cols-[1fr_auto] gap-x-8 gap-y-1 text-sm"
    >
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt
            className={row.muted ? "text-muted-foreground" : "text-foreground"}
          >
            {row.label}
          </dt>
          <dd
            className={`text-right tabular-nums ${row.emphasis ? "font-semibold" : ""}`}
          >
            {formatQuotedPrice(row.pence)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2" aria-label={title}>
      <h2 className="text-base font-medium">{title}</h2>
      {children}
    </section>
  );
}

/** Money in against the document: a card payment, a bank transfer, the ordering channel's. */
export function PaymentsBlock({
  payments,
}: {
  payments: OrderPaymentReceivedView[];
}) {
  return (
    <Section title="Payments received">
      {payments.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing received yet.</p>
      ) : (
        <ul className="divide-y rounded-md border text-sm">
          {payments.map((p, i) => (
            <li
              key={`${p.method}-${p.received_on}-${i}`}
              className="flex flex-wrap justify-between gap-x-4 gap-y-1 px-3 py-2"
            >
              <span>
                {p.method_label}
                {p.reference && (
                  <span className="text-muted-foreground">
                    {" "}
                    · {p.reference}
                  </span>
                )}
                <span className="text-muted-foreground">
                  {" "}
                  · {formatShortDateOrDash(p.received_on)}
                </span>
              </span>
              <span className="tabular-nums">
                {formatQuotedPrice(p.amount_pence)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** The credit notes raised on an invoice, each opening its own page. */
export function CreditNotesBlock({
  creditNotes,
}: {
  creditNotes: OrderCreditNoteView[];
}) {
  if (creditNotes.length === 0) return null;
  return (
    <Section title="Credit notes">
      <ul className="divide-y rounded-md border text-sm">
        {creditNotes.map((n) => (
          <li
            key={n.credit_note_no}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2"
          >
            <span className="flex flex-wrap items-center gap-2">
              <Link
                to={paths.creditNoteDetail({ creditNoteNo: n.credit_note_no })}
                className="font-medium hover:underline"
              >
                {n.credit_note_no}
              </Link>
              <span className="text-muted-foreground">
                Lines {n.line_nos.join(", ")} ·{" "}
                {formatShortDateOrDash(n.issued_on)}
              </span>
              <StateBadge state={n.state} label={n.state_label} />
            </span>
            <span className="tabular-nums">
              {formatQuotedPrice(-n.gross_pence)}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ── Loading ──────────────────────────────────────────────────────────────

export type DocumentLoad<T> =
  | { kind: "loading" }
  | { kind: "not_found" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; page: T };

/** Read one document's page; null from the adapter is "not found". `reload` re-reads without a loading flash. */
export function useDocumentPage<T>(
  load: () => Promise<T | null>,
  deps: readonly unknown[],
): { state: DocumentLoad<T>; reload: () => Promise<void> } {
  const [state, setState] = useState<DocumentLoad<T>>({ kind: "loading" });
  // Only the latest read may answer: a slower one for a document left behind is dropped.
  const latest = useRef(0);
  const read = useCallback(async () => {
    const request = ++latest.current;
    try {
      const page = await load();
      if (request === latest.current)
        setState(page ? { kind: "loaded", page } : { kind: "not_found" });
    } catch (err) {
      if (request === latest.current)
        setState({
          kind: "failed",
          message:
            err instanceof Error ? err.message : "The page did not load.",
        });
    }
    // The loader closes over the document number; `deps` names it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    setState({ kind: "loading" });
    void read();
    return () => {
      latest.current += 1;
    };
  }, [read]);
  return { state, reload: read };
}

/** What a page shows while it has no document: the loading skeleton, the failure, or the 404. */
export function DocumentPageStatus({
  state,
  what,
  documentNo,
}: {
  state: DocumentLoad<unknown>;
  what: string;
  documentNo: string;
}) {
  if (state.kind === "loading")
    return (
      <div className="space-y-3" aria-label={`Loading ${what} ${documentNo}`}>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (state.kind === "failed")
    return (
      <Alert variant="destructive" role="alert">
        <AlertDescription>
          Could not load {what} {documentNo}: {state.message}
        </AlertDescription>
      </Alert>
    );
  if (state.kind === "not_found")
    return (
      <Card className="p-12 text-center">
        <h1 className="mb-2 text-lg">
          {what[0]!.toUpperCase()}
          {what.slice(1)} not found
        </h1>
        <p className="text-muted-foreground">
          There is no {what} {documentNo} that you can see.
        </p>
      </Card>
    );
  return null;
}

// ── View PDF ─────────────────────────────────────────────────────────────

/** View PDF: fetch the document's PDF (auth-gated) and save it; `notice` says why it did not. */
export function usePdfDownload() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const viewPdf = useCallback(
    async (pdfUrl: string) => {
      setBusy(true);
      setNotice(null);
      try {
        const { blob, filename } =
          await adapter.downloadSalesDocumentPdf(pdfUrl);
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setNotice(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );
  return { busy, notice, setNotice, viewPdf };
}
